/**
 * Paper tear — the hero rips down the middle to uncover the next section.
 *
 * The hero is pinned for a stretch of scrolling while its two halves slide
 * apart along a ragged seam; the section passed as `children` sits pinned
 * underneath for the same stretch, so it is simply there once the paper has
 * gone. Pinning is CSS `position: sticky` (it stays smooth on a phone, where
 * scroll runs off the main thread); this component only feeds scroll progress
 * to the `--tear-p` custom property that elf-landing.css reads.
 *
 * One element cannot move in two directions, so the right-hand half is a DOM
 * snapshot of the hero taken as the tear starts and dropped when the page is
 * back at the top. A snapshot rather than a second React render: it captures
 * whatever state the visitor left the hero in — a flipped pass, a spun record.
 * At rest there is exactly one hero and it behaves as it always did.
 */

import React, { useEffect, useRef, type ReactNode } from 'react';

/** Seam resolution: straight runs between this many ragged points. */
const SEAM_STEPS = 24;
/** How far, in px, the seam may wander either side of centre. */
const SEAM_WANDER = 11;
/** Longest we hold the tear back waiting for the snapshot's images. */
const DECODE_WAIT_MS = 160;

/** Small seeded PRNG — the seam must be identical on both halves and renders. */
function prng(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** `clip-path` polygons for the two halves and the paper edge behind each. */
function buildSeam(): Record<'--tear-l' | '--tear-r' | '--tear-el' | '--tear-er', string> {
  const rand = prng(20261005);
  const left: string[] = [];
  const right: string[] = [];
  const edgeLeft: string[] = [];
  const edgeRight: string[] = [];
  let x = 0;
  for (let i = 0; i <= SEAM_STEPS; i += 1) {
    const last = i === 0 || i === SEAM_STEPS;
    const y = last ? (i / SEAM_STEPS) * 100 : ((i + (rand() - 0.5) * 0.7) / SEAM_STEPS) * 100;
    x = Math.max(-SEAM_WANDER, Math.min(SEAM_WANDER, x + (rand() - 0.5) * SEAM_WANDER * 1.5));
    const fibre = 3 + rand() * 5;
    const at = (dx: number) => `calc(50% + ${(x + dx).toFixed(1)}px) ${y.toFixed(2)}%`;
    left.push(at(0));
    right.push(at(0));
    edgeLeft.push(at(fibre));
    edgeRight.push(at(-fibre));
  }
  return {
    '--tear-l': `polygon(0 0, ${left.join(', ')}, 0 100%)`,
    '--tear-r': `polygon(100% 0, ${right.join(', ')}, 100% 100%)`,
    '--tear-el': `polygon(0 0, ${edgeLeft.join(', ')}, 0 100%)`,
    '--tear-er': `polygon(100% 0, ${edgeRight.join(', ')}, 100% 100%)`,
  };
}

const SEAM = buildSeam();

/**
 * Scroll to where the tear has finished and the next section is clear.
 * `scrollIntoView` cannot do this: while the hero is up, that section is
 * already pinned at the top of the viewport, so the browser sees nothing to do.
 */
export function scrollPastHero(): void {
  const root = document.querySelector<HTMLElement>('.elf-tear');
  if (!root) {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
    return;
  }
  const distance = root.querySelector<HTMLElement>('.elf-tear__run')?.offsetHeight ?? 0;
  window.scrollTo({
    top: root.getBoundingClientRect().top + window.scrollY + distance,
    behavior: 'smooth',
  });
}

/** Bring the snapshot's CSS animations to the same moment as the original's. */
function syncAnimations(source: HTMLElement, copy: HTMLElement): void {
  if (typeof copy.getAnimations !== 'function') return;
  const from = [source, ...Array.from(source.querySelectorAll('*'))];
  const to = [copy, ...Array.from(copy.querySelectorAll('*'))];
  const original = new Map<Element, Element>();
  to.forEach((el, i) => original.set(el, from[i]));

  for (const anim of copy.getAnimations({ subtree: true })) {
    const effect = anim.effect as KeyframeEffect | null;
    const target = effect?.target ? original.get(effect.target) : undefined;
    const name = (anim as CSSAnimation).animationName;
    const twin = target
      ?.getAnimations()
      .find(
        (a) =>
          (a as CSSAnimation).animationName === name &&
          (a.effect as KeyframeEffect | null)?.pseudoElement === effect?.pseudoElement,
      );
    try {
      if (twin) anim.currentTime = twin.currentTime;
      // No twin means the original's has run its course — an entrance, usually.
      else anim.finish();
    } catch {
      // Endless animations cannot be finished; they are left to run.
    }
  }
}

/** A canvas clones empty — paint the original's current frame onto the copy. */
function copyCanvases(source: HTMLElement, copy: HTMLElement): void {
  const from = source.querySelectorAll('canvas');
  copy.querySelectorAll('canvas').forEach((canvas, i) => {
    const original = from[i];
    if (!original || !original.width || !original.height) return;
    if (canvas.width !== original.width) canvas.width = original.width;
    if (canvas.height !== original.height) canvas.height = original.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(original, 0, 0);
  });
}

interface ElfTearProps {
  /** The sheet that tears — the hero. */
  hero: ReactNode;
  /** What the tear uncovers. */
  children: ReactNode;
}

export const ElfTear: React.FC<ElfTearProps> = ({ hero, children }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const cloneRef = useRef<HTMLDivElement>(null);
  const runRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const pin = pinRef.current;
    const stage = stageRef.current;
    const sheet = sheetRef.current;
    const holder = cloneRef.current;
    const run = runRef.current;
    if (!root || !pin || !stage || !sheet || !holder || !run) return;

    for (const [name, value] of Object.entries(SEAM)) root.style.setProperty(name, value);

    /** The hero the snapshot was taken from, and the snapshot itself. */
    let source: HTMLElement | null = null;
    let copy: HTMLElement | null = null;
    let ready = false;
    let frame = 0;
    /** Scroll the hero does on its own before pinning, when taller than the screen. */
    let lead = 0;
    /** Scroll distance the tear itself takes. */
    let distance = 1;

    const drop = () => {
      holder.replaceChildren();
      source = null;
      copy = null;
      ready = false;
      root.classList.remove('is-tearing');
    };

    const snapshot = (from: HTMLElement) => {
      drop();
      source = from;
      const made = from.cloneNode(true) as HTMLElement;
      made.removeAttribute('id');
      made.setAttribute('aria-hidden', 'true');
      made.setAttribute('inert', '');
      copy = made;
      holder.appendChild(made);

      // Hold the tear until the copy can paint, or one frame would show the
      // right half missing. Bounded: a slow decode must not stall the scroll.
      const decoded = Promise.all(
        Array.from(made.querySelectorAll('img')).map((img) => img.decode?.().catch(() => undefined)),
      );
      const timeout = new Promise((resolve) => window.setTimeout(resolve, DECODE_WAIT_MS));
      void Promise.race([decoded, timeout]).then(() => {
        if (copy !== made) return;
        syncAnimations(from, made);
        ready = true;
        update();
      });
    };

    const measure = () => {
      root.style.setProperty('--tear-h', `${stage.offsetHeight}px`);
      lead = Math.max(0, -(parseFloat(getComputedStyle(pin).top) || 0));
      distance = Math.max(1, run.offsetHeight - lead);
    };

    function update() {
      frame = 0;
      const scrolled = -root!.getBoundingClientRect().top - lead;
      const p = Math.max(0, Math.min(1, scrolled / distance));
      const current = sheet!.firstElementChild as HTMLElement | null;

      if (p <= 0 || p >= 1 || !current) {
        if (copy) drop();
        root!.classList.toggle('is-open', p >= 1);
        return;
      }
      root!.classList.remove('is-open');
      // The visitor switched hero mid-tear: the old snapshot is of another sheet.
      if (source !== current) snapshot(current);
      if (!ready || !copy) return;
      copyCanvases(current, copy);
      root!.style.setProperty('--tear-p', p.toFixed(4));
      root!.classList.add('is-tearing');
    }

    const queue = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const onResize = () => {
      measure();
      queue();
    };

    const sizes = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    sizes?.observe(stage);
    measure();
    update();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(frame);
      sizes?.disconnect();
      window.removeEventListener('scroll', queue);
      window.removeEventListener('resize', onResize);
      drop();
      root.classList.remove('is-open');
    };
  }, []);

  return (
    <div ref={rootRef} className="elf-tear">
      <div ref={pinRef} className="elf-tear__pin">
        <div ref={stageRef} className="elf-tear__stage">
          <div className="elf-tear__edge elf-tear__edge--l" aria-hidden="true" />
          <div className="elf-tear__edge elf-tear__edge--r" aria-hidden="true" />
          <div ref={sheetRef} className="elf-tear__sheet">
            {hero}
          </div>
          <div ref={cloneRef} className="elf-tear__clone" />
        </div>
      </div>
      <div className="elf-tear__next">{children}</div>
      <div ref={runRef} className="elf-tear__run" aria-hidden="true" />
    </div>
  );
};

export default ElfTear;
